/**
 * Light / dark theme.
 *
 * `prefers-color-scheme` decides by default, a manual choice overrides it and is
 * persisted. Three states, not two: "Auto" has to remain reachable, otherwise a user
 * who once clicked the toggle is stuck with a fixed theme forever.
 */

import { computed, ref } from 'vue'

const STORAGE_KEY = 'previewapp.theme'

/** @type {import('vue').Ref<'auto'|'light'|'dark'>} */
const preference = ref(readStoredPreference())

function readStoredPreference() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'auto'
  } catch {
    return 'auto'
  }
}

function apply(value) {
  const root = document.documentElement
  if (value === 'auto') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', value)
}

function persist(value) {
  try {
    if (value === 'auto') localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Private mode: the theme simply does not survive a reload.
  }
}

const LABELS = { auto: 'Automatic theme', light: 'Light theme', dark: 'Dark theme' }
const ORDER = ['auto', 'light', 'dark']

export function useTheme() {
  const label = computed(() => LABELS[preference.value])

  /** @param {'auto'|'light'|'dark'} value */
  function set(value) {
    preference.value = value
    apply(value)
    persist(value)
  }

  /** Cycles auto -> light -> dark. */
  function cycle() {
    set(ORDER[(ORDER.indexOf(preference.value) + 1) % ORDER.length])
  }

  /** Called once at boot, to reconcile the DOM with what was stored. */
  function initialize() {
    apply(preference.value)
  }

  return { preference, label, set, cycle, initialize }
}
